import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CaptchaProvider } from "@/context/CaptchaContext";
import { TrackingProvider } from "@/context/TrackingContext";
import "@/App.css";

// Public pages
import Landing from "@/pages/Landing";
import Discovery from "@/pages/Discovery";
import DiscoverySwipe from "@/pages/DiscoverySwipe";
import ListenerProfile from "@/pages/ListenerProfile";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ListenerRegister from "@/pages/ListenerRegister";
import Participate from "@/pages/Participate";
import PaymentSuccess from "@/pages/PaymentSuccess";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TermsOfUse from "@/pages/TermsOfUse";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import SuggestionPayment from "@/pages/SuggestionPayment";

// User pages
import UserDashboard from "@/pages/user/Dashboard";

// Listener pages
import ListenerDashboard from "@/pages/listener/Dashboard";
import ListenerProfileEdit from "@/pages/listener/ProfileEdit";
import ListenerAvailability from "@/pages/listener/Availability";
import ListenerSessions from "@/pages/listener/Sessions";
import ListenerSettings from "@/pages/listener/Settings";
import ListenerUserNotes from "@/pages/listener/UserNotes";

// Admin pages
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminListeners from "@/pages/admin/Listeners";
import AdminUsers from "@/pages/admin/Users";
import AdminSessions from "@/pages/admin/Sessions";
import AdminInvitations from "@/pages/admin/Invitations";
import AdminRequests from "@/pages/admin/Requests";
import AdminTracking from "@/pages/admin/Tracking";
import AdminAnalytics from "@/pages/admin/Analytics";
import AdminChatbot from "@/pages/admin/Chatbot";
import AdminPromoters from "@/pages/admin/Promoters";

// Protected Route component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    // Redirect based on role
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    if (user.role === "listener") return <Navigate to="/listener" replace />;
    return <Navigate to="/mis-sesiones" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <CaptchaProvider>
        <TrackingProvider>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/explorar" element={<Discovery />} />
            <Route path="/descubrir" element={<DiscoverySwipe />} />
            <Route path="/l/:username" element={<ListenerProfile />} />
            <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Register />} />
          <Route path="/registro-listener" element={<ListenerRegister />} />
          <Route path="/participar" element={<Participate />} />
          <Route path="/pago-exitoso" element={<PaymentSuccess />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/olvide-contrasena" element={<ForgotPassword />} />
          <Route path="/restablecer-contrasena" element={<ResetPassword />} />
          <Route path="/terminos" element={<TermsOfUse />} />
          <Route path="/privacidad" element={<PrivacyPolicy />} />
          <Route path="/pagar-sugerencia/:suggestionId" element={<SuggestionPayment />} />
          
          {/* User routes */}
          <Route path="/mis-sesiones" element={
            <ProtectedRoute allowedRoles={["usuario"]}>
              <UserDashboard />
            </ProtectedRoute>
          } />
          
          {/* Listener routes */}
          <Route path="/listener" element={
            <ProtectedRoute allowedRoles={["listener"]}>
              <ListenerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/listener/perfil" element={
            <ProtectedRoute allowedRoles={["listener"]}>
              <ListenerProfileEdit />
            </ProtectedRoute>
          } />
          <Route path="/listener/disponibilidad" element={
            <ProtectedRoute allowedRoles={["listener"]}>
              <ListenerAvailability />
            </ProtectedRoute>
          } />
          <Route path="/listener/sesiones" element={
            <ProtectedRoute allowedRoles={["listener"]}>
              <ListenerSessions />
            </ProtectedRoute>
          } />
          <Route path="/listener/usuarios" element={
            <ProtectedRoute allowedRoles={["listener"]}>
              <ListenerUserNotes />
            </ProtectedRoute>
          } />
          <Route path="/listener/configuracion" element={
            <ProtectedRoute allowedRoles={["listener"]}>
              <ListenerSettings />
            </ProtectedRoute>
          } />
          
          {/* Admin routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/listeners" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminListeners />
            </ProtectedRoute>
          } />
          <Route path="/admin/usuarios" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminUsers />
            </ProtectedRoute>
          } />
          <Route path="/admin/sesiones" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminSessions />
            </ProtectedRoute>
          } />
          <Route path="/admin/invitaciones" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminInvitations />
            </ProtectedRoute>
          } />
          <Route path="/admin/solicitudes" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminRequests />
            </ProtectedRoute>
          } />
          <Route path="/admin/tracking" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminTracking />
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminAnalytics />
            </ProtectedRoute>
          } />
          <Route path="/admin/chatbot" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminChatbot />
            </ProtectedRoute>
          } />
          
          <Route path="/admin/promotores" element={
            <ProtectedRoute allowedRoles={["admin"]}>
              <AdminPromoters />
            </ProtectedRoute>
          } />
          
          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        </TrackingProvider>
      </CaptchaProvider>
    </AuthProvider>
  );
}

export default App;
